import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ArrowLeftIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useLocation } from '../contexts/LocationContext';
import { useBusinessTypeContext } from '../contexts/BusinessTypeContext';
import { usePonds, useMutation } from '../hooks/useApi';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import apiClient, { getErrorMessage } from '../utils/api';
import { PaymentRequestCreateForm } from '../types/api';

interface PaymentRequestFormData {
  request_date: string;
  amount: number;
  expense_type: string;
  description: string;
  upi_id: string;
  name_on_upi: string;
  qr_image_base64?: string;  // QR code image in base64 format
  business_type: 'fish' | 'goat';
  pond_id?: string;
  shed_id?: string;
}

const paymentRequestSchema = yup.object({
  request_date: yup.string().required('Request date is required'),
  amount: yup.number().positive('Amount must be positive').required('Amount is required'),
  expense_type: yup.string().required('Please select expense type'),
  description: yup.string().required('Description is required'),
  upi_id: yup.string().required('UPI ID or phone number is required'),
  name_on_upi: yup.string().required('Name on UPI is required'),
  business_type: yup.string().oneOf(['fish', 'goat'], 'Business type must be fish or goat').required('Business type is required'),
  pond_id: yup.string().optional(),
  shed_id: yup.string().optional(),
});

const PaymentRequestForm: React.FC = () => {
  const { selectedLocation } = useLocation();
  const { businessType: contextBusinessType } = useBusinessTypeContext();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEditMode = !!id;
  const [error, setError] = useState('');
  const [sheds, setSheds] = useState<any[]>([]);
  const [qrUploading, setQrUploading] = useState(false);
  const [loading, setLoading] = useState(isEditMode);

  const { data: ponds } = usePonds(selectedLocation?.id || null);
  const { mutate: createPaymentRequest, loading: creating } = useMutation<any, PaymentRequestCreateForm>();

  const form = useForm<PaymentRequestFormData>({
    resolver: yupResolver(paymentRequestSchema) as any,
    defaultValues: {
      request_date: new Date().toISOString().split('T')[0],
      amount: 0,
      expense_type: '',
      description: '',
      upi_id: '',
      name_on_upi: '',
      business_type: contextBusinessType,
      pond_id: '',
      shed_id: '',
    }
  });

  // Load existing request data if editing
  useEffect(() => {
    if (isEditMode && id) {
      loadRequestData(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, id]);

  const loadRequestData = async (requestId: string) => {
    try {
      setLoading(true);
      setError('');
      const response = await apiClient.get(`/payment-requests/${requestId}?include_qr_images=true`);
      const request = response.data;
      
      // Populate form with existing data
      form.reset({
        request_date: request.request_date.split('T')[0],
        amount: request.amount,
        expense_type: request.expense_type,
        description: request.description,
        upi_id: request.upi_id,
        name_on_upi: request.name_on_upi || '',
        qr_image_base64: request.qr_image_base64 || '',
        business_type: request.business_type,
        pond_id: request.pond_id || '',
        shed_id: request.shed_id || '',
      });
      
      // Load sheds if business type is goat
      if (request.business_type === 'goat' && selectedLocation?.id) {
        loadGoatSheds();
      }
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to load payment request'));
    } finally {
      setLoading(false);
    }
  };

  const loadGoatSheds = async () => {
    try {
      const response = await apiClient.get(`/goat/sheds?location_id=${selectedLocation?.id}`);
      setSheds(response.data || []);
    } catch (err) {
      console.error('Failed to load goat sheds:', err);
    }
  };

  // Auto-set business type from context when it changes
  useEffect(() => {
    if (!isEditMode) {
      form.setValue('business_type', contextBusinessType);
      // Reset expense_type when business type changes
      form.setValue('expense_type', '');
    }
  }, [contextBusinessType, form, isEditMode]);

  const businessType = form.watch('business_type');

  // Load goat sheds when business type changes to goat (from form or context)
  useEffect(() => {
    if (selectedLocation?.id && businessType === 'goat') {
      loadGoatSheds();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation?.id, businessType]);

  // Expense types available for payment requests
  // For fish: salary, maintenance, diesel, misc (feed/medicine excluded, construction/goat_purchase excluded)
  // For goat: feed, medicine, salary, maintenance, construction, goat_purchase, misc (diesel excluded)
  const expenseTypes = {
    fish: [
      { value: 'salary', label: 'Salary' },
      { value: 'maintenance', label: 'Maintenance' },
      { value: 'diesel', label: 'Diesel/Fuel' },
      { value: 'misc', label: 'Miscellaneous' },
    ],
    goat: [
      { value: 'feed', label: 'Feed' },
      { value: 'medicine', label: 'Medicine' },
      { value: 'salary', label: 'Salary' },
      { value: 'maintenance', label: 'Maintenance' },
      { value: 'construction', label: 'Construction' },
      { value: 'goat_purchase', label: 'Goat purchase' },
      { value: 'misc', label: 'Miscellaneous' },
    ],
  };

  const handleQRUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setQrUploading(true);
      setError('');

      // Convert file to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64String = reader.result as string;
          
          // Store the QR image in the form
          form.setValue('qr_image_base64', base64String);
          
          // Call backend to parse QR code
          const response = await apiClient.post('/payment-requests/parse-qr', {
            qr_image_base64: base64String
          });
          
          if (response.data?.upi_id) {
            form.setValue('upi_id', response.data.upi_id);
            // Also set name_on_upi if extracted from QR code
            if (response.data?.name_on_upi) {
              form.setValue('name_on_upi', response.data.name_on_upi);
            }
          } else {
            setError('Could not extract UPI ID from QR code. Please ensure the QR code is clear and contains UPI payment information.');
          }
        } catch (err: any) {
          setError(getErrorMessage(err, 'Failed to parse QR code'));
        } finally {
          setQrUploading(false);
        }
      };
      
      reader.readAsDataURL(file);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to upload QR code'));
      setQrUploading(false);
    }
  };

  const handleSubmit = async (data: PaymentRequestFormData) => {
    try {
      setError('');
      
      if (isEditMode && id) {
        // Update existing request
        const updateData: any = {
          request_date: data.request_date,
          amount: data.amount,
          expense_type: data.expense_type,
          description: data.description,
          upi_id: data.upi_id,
          name_on_upi: data.name_on_upi,
          business_type: data.business_type,
        };
        
        if (data.qr_image_base64) {
          updateData.qr_image_base64 = data.qr_image_base64;
        }
        
        if (data.business_type === 'fish') {
          updateData.pond_id = data.pond_id || null;
          updateData.shed_id = null;
        } else {
          updateData.shed_id = data.shed_id || null;
          updateData.pond_id = null;
        }
        
        await apiClient.put(`/payment-requests/${id}`, updateData);
        navigate('/payment-requests');
      } else {
        // Create new request
        const requestData: PaymentRequestCreateForm = {
          location_id: selectedLocation!.id,
          request_date: data.request_date,
          amount: data.amount,
          expense_type: data.expense_type as any,
          description: data.description,
          upi_id: data.upi_id,
          name_on_upi: data.name_on_upi,
          qr_image_base64: data.qr_image_base64 || undefined,
          business_type: data.business_type,
          pond_id: data.business_type === 'fish' ? (data.pond_id || undefined) : undefined,
          shed_id: data.business_type === 'goat' ? (data.shed_id || undefined) : undefined,
        };

        const newRequest = await createPaymentRequest('/payment-requests', requestData);
        if (newRequest) {
          navigate('/payment-requests');
        }
      }
    } catch (err: any) {
      setError(getErrorMessage(err, isEditMode ? 'Failed to update payment request' : 'Failed to create payment request'));
    }
  };

  if (loading) {
    return (
      <div className="container-mobile py-8">
        <div className="flex justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (!selectedLocation) {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">
          Go to Location Selection
        </Link>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link to="/payment-requests" className="btn-touch p-2 text-gray-400 hover:text-gray-600">
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? '✏️ Edit Payment Request' : '💰 New Payment Request'}
          </h1>
          <p className="text-gray-600">{selectedLocation.name}</p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Payment Request Form */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <PlusIcon className="h-5 w-5 mr-2" />
          {isEditMode ? 'Edit Payment Request' : 'Create Payment Request'}
        </h2>

        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Request Date *</label>
              <input
                type="date"
                {...form.register('request_date')}
                className="form-input"
              />
              {form.formState.errors.request_date && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.request_date.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">Amount (₹) *</label>
              <input
                type="number"
                step="0.01"
                {...form.register('amount')}
                className="form-input"
                placeholder="0.00"
              />
              {form.formState.errors.amount && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.amount.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">Business Type *</label>
              <select 
                {...form.register('business_type')} 
                className="form-input"
                onChange={(e) => {
                  form.setValue('business_type', e.target.value as 'fish' | 'goat');
                  form.setValue('expense_type', ''); // Reset expense type when business type changes
                  form.setValue('pond_id', ''); // Clear pond_id
                  form.setValue('shed_id', ''); // Clear shed_id
                }}
              >
                <option value="fish">Fish</option>
                <option value="goat">Goat</option>
              </select>
              {form.formState.errors.business_type && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.business_type.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">Expense Type *</label>
              <select {...form.register('expense_type')} className="form-input">
                <option value="">Select Type</option>
                {expenseTypes[businessType].map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              {form.formState.errors.expense_type && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.expense_type.message}</p>
              )}
            </div>

            {businessType === 'fish' ? (
              <div>
                <label className="form-label">Associated Pond</label>
                <select {...form.register('pond_id')} className="form-input">
                  <option value="">General (Not pond-specific)</option>
                  {ponds?.map((pond) => (
                    <option key={pond.id} value={pond.id}>
                      {pond.name} - {pond.pond_type === 'nursery' ? 'Nursery' : 'Grower'}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <label className="form-label">Associated Shed</label>
                <select {...form.register('shed_id')} className="form-input">
                  <option value="">General (Not shed-specific)</option>
                  {sheds.map((shed) => (
                    <option key={shed.id} value={shed.id}>
                      {shed.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="sm:col-span-2">
              <label className="form-label">Description *</label>
              <textarea
                {...form.register('description')}
                className="form-input"
                rows={2}
                placeholder="What is this payment request for? (e.g., Bike repair, Construction)"
              />
              {form.formState.errors.description && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.description.message}</p>
              )}
            </div>

            <div className="sm:col-span-2">
              <label className="form-label">UPI ID or Phone Number *</label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  {...form.register('upi_id')}
                  className="form-input flex-1"
                  placeholder="manager@paytm or 9876543210"
                />
                <label className="btn-touch bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 cursor-pointer flex items-center">
                  {qrUploading ? (
                    <>
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">Uploading...</span>
                    </>
                  ) : (
                    <>
                      <span>📷 QR</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleQRUpload}
                        className="hidden"
                        disabled={qrUploading}
                      />
                    </>
                  )}
                </label>
              </div>
              {form.formState.errors.upi_id && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.upi_id.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Enter UPI ID (e.g., manager@paytm) or phone number (e.g., 9876543210). Or upload QR code to extract UPI ID.
              </p>
            </div>

            <div className="sm:col-span-2">
              <label className="form-label">Name on UPI *</label>
              <input
                type="text"
                {...form.register('name_on_upi')}
                className="form-input"
                placeholder="Enter the name associated with this UPI ID"
              />
              {form.formState.errors.name_on_upi && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.name_on_upi.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Enter the name that appears when you send money to this UPI ID (helps verify the correct recipient).
              </p>
            </div>
          </div>

          <button
            type="submit"
            disabled={creating}
            className="w-full btn-touch bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
          >
            {creating ? <LoadingSpinner size="sm" /> : (isEditMode ? 'Update Payment Request' : 'Create Payment Request')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PaymentRequestForm;

